import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function PrayerRequestsPage() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    pedido: '',
    privacidade: 'publico'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3pbusmvn82kpvfp3eu0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          pedido: '',
          privacidade: 'publico'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappNumber = '+351910969876';
  const whatsappMessage = 'Olá! Gostaria de fazer um pedido de oração.';
  const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <img 
                src="https://static.readdy.ai/image/8fbfc975813ef59021c9f16b313e86f9/f5810bc36c96b4c4a03e9a9aa341e507.png"
                alt="Assembleia de Deus - Missão Europa"
                className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 object-contain shadow-2xl rounded-2xl border-4 border-indigo-200"
              />
              <div className="text-center flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent whitespace-nowrap">
                  <span style={{ fontFamily: '"Agnes", serif' }}>IGREJA AD MISSÃO EUROPA CHURCH</span>
                </h1>
                <p className="text-sm text-gray-600">Church Portugal</p>
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors">
                Início
              </Link>
              <Link to="/bible" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors">
                Bíblia
              </Link>
              <Link to="/devotionals" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors">
                Devocionais
              </Link>
              <Link to="/studies" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium transition-colors">
                Estudos
              </Link>
              <Link to="/prayer-requests" className="text-blue-600 border-b-2 border-blue-600 px-3 py-2 rounded-md text-sm font-medium">
                Pedidos de Oração
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-hands-pray-line text-2xl text-white"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Pedidos de Oração</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Compartilhe seus pedidos de oração conosco. Nossa comunidade está aqui para orar por você e apoiá-lo em suas necessidades.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Prayer Request Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-edit-line text-blue-600 mr-3"></i>
              Enviar Pedido de Oração
            </h2>

            <form onSubmit={handleSubmit} data-readdy-form id="pedidos-oracao">
              <div className="space-y-6">
                <div>
                  <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Digite seu nome completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="+351 xxx xxx xxx"
                  />
                </div>

                <div>
                  <label htmlFor="pedido" className="block text-sm font-medium text-gray-700 mb-2">
                    Pedido de Oração *
                  </label>
                  <textarea
                    id="pedido"
                    name="pedido"
                    value={formData.pedido}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                    placeholder="Compartilhe seu pedido de oração conosco..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.pedido.length}/500 caracteres
                  </p>
                </div>

                <div>
                  <label htmlFor="privacidade" className="block text-sm font-medium text-gray-700 mb-2">
                    Privacidade do Pedido
                  </label>
                  <select
                    id="privacidade"
                    name="privacidade"
                    value={formData.privacidade}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="publico">Público - Pode ser compartilhado com a comunidade</option>
                    <option value="privado">Privado - Apenas para os líderes da igreja</option>
                    <option value="anonimo">Anônimo - Sem identificação pessoal</option>
                  </select>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-check-circle-line text-green-600 mr-2"></i>
                      <p className="text-green-800 text-sm">
                        Seu pedido de oração foi enviado com sucesso! Estaremos orando por você.
                      </p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 mr-2"></i>
                      <p className="text-red-800 text-sm">
                        Ocorreu um erro ao enviar seu pedido. Tente novamente ou use uma das opções de contato abaixo.
                      </p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || formData.pedido.length > 500}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <i className="ri-send-plane-line mr-2"></i>
                      Enviar Pedido de Oração
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Contact Options */}
          <div className="space-y-6">
            {/* WhatsApp Contact */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-whatsapp-line text-green-600 mr-3"></i>
                WhatsApp
              </h3>
              <p className="text-gray-600 mb-6">
                Envie seu pedido de oração diretamente pelo WhatsApp para um atendimento mais rápido e pessoal.
              </p>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-fill mr-2"></i>
                Enviar via WhatsApp
              </a>
              <p className="text-sm text-gray-500 mt-3 text-center">
                {whatsappNumber}
              </p>
            </div>

            {/* Phone Contact */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-phone-line text-blue-600 mr-3"></i>
                Telefone
              </h3>
              <p className="text-gray-600 mb-6">
                Ligue diretamente para conversar com um de nossos pastores ou líderes sobre seu pedido de oração.
              </p>
              <a
                href={`tel:${whatsappNumber}`}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill mr-2"></i>
                Ligar Agora
              </a>
              <p className="text-sm text-gray-500 mt-3 text-center">
                {whatsappNumber}
              </p>
            </div>

            {/* Prayer Guidelines */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-heart-line text-red-500 mr-3"></i>
                Como Oramos Por Você
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm">Oramos diariamente por todos os pedidos recebidos</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm">Pedidos urgentes são priorizados</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm">Mantemos confidencialidade total</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mr-2 mt-1 flex-shrink-0"></i>
                  <span className="text-sm">Oferecemos acompanhamento pastoral quando necessário</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bible Verse */}
        <div className="mt-12 text-center bg-white rounded-xl shadow-lg p-8">
          <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-book-open-line text-white text-xl"></i>
          </div>
          <blockquote className="text-xl italic text-gray-700 mb-4">
            "Não andeis ansiosos por coisa alguma; antes, em tudo, sejam conhecidas, diante de Deus, as vossas petições, pela oração e pela súplica, com ações de graças."
          </blockquote>
          <cite className="text-blue-600 font-medium">Filipenses 4:6</cite>
        </div>
      </main>
    </div>
  );
}